import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon, PageHeader, StatusBadge } from '@carrent/ui'
import { api } from '@carrent/api'

// Эмодзи как иконки бриф запрещает, и здесь они к тому же дублировали
// StatusBadge, стоящий в той же строке. Осталась цветная полоса —
// она одна и несёт статус на уровне беглого взгляда.
const STATUS_LABELS = {
  active:             { label: 'В пути',        color: 'var(--accent)' },
  finished_by_user:   { label: 'Ждёт проверки', color: 'var(--warning)' },
  confirmed_by_admin: { label: 'Подтверждена',  color: 'var(--success)' },
}

function TripCard({ trip, onClick }) {
  const meta  = STATUS_LABELS[trip.status] || {}
  // Нормализуем к UTC — добавляем Z если нет timezone суффикса
  const norm  = (s) => s && (/[Z+]/.test(s) ? s : s + 'Z')
  const start = new Date(norm(trip.started_at))
  const end   = trip.finished_at ? new Date(norm(trip.finished_at)) : null

  // Длительность
  const durMs  = Math.max(0, end ? end - start : Date.now() - start)
  const durH   = Math.floor(durMs / 3_600_000)
  const durMin = Math.floor((durMs % 3_600_000) / 60_000)
  const durStr = durH > 0 ? `${durH} ч ${durMin} мин` : `${durMin} мин`

  const carLabel = trip.car
    ? `${trip.car.brand} ${trip.car.model}`
    : `Машина #${trip.car_id}`

  const plateBadge = trip.car?.plate

  return (
    <div className="card" style={{ cursor: onClick ? 'pointer' : 'default', padding: 0, overflow: 'hidden' }}
      onClick={onClick}>

      {/* Цветная полоса по статусу */}
      <div style={{ height: 3, background: meta.color || 'var(--border)' }} />

      <div style={{ padding: 'var(--gap-sm) var(--gap)' }}>
        {/* Шапка: машина + статус */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div>
            <p style={{ fontWeight: 700, fontSize: '1rem' }}>{carLabel}</p>
            {plateBadge && (
              <span style={{
                display: 'inline-block', background: '#fff', color: '#000',
                padding: '1px 8px', borderRadius: 4, fontSize: '0.72rem',
                fontWeight: 800, letterSpacing: '0.08em', marginTop: 3,
                border: '1px solid #333', fontFamily: 'monospace',
              }}>
                {plateBadge}
              </span>
            )}
          </div>
          <StatusBadge status={trip.status} />
        </div>

        {/* Даты */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <p className="text-muted" style={{ fontSize: '0.8rem' }}>
            Начало: {start.toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })}
          </p>
          {end && (
            <p className="text-muted" style={{ fontSize: '0.8rem' }}>
              Конец: {end.toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
          )}
        </div>

        {/* Статистика */}
        <div style={{ display: 'flex', gap: 'var(--gap)', marginTop: 8,
          paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '1rem', fontWeight: 700 }}>
              {trip.distance != null ? `${trip.distance.toLocaleString()}` : '—'}
            </p>
            <p className="text-muted" style={{ fontSize: '0.72rem' }}>км</p>
          </div>
          <div style={{ width: 1, background: 'var(--border)' }} />
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '1rem', fontWeight: 700 }}>{durStr}</p>
            <p className="text-muted" style={{ fontSize: '0.72rem' }}>
              {trip.status === 'active' ? 'в пути' : 'длительность'}
            </p>
          </div>
          {trip.end_mileage && (
            <>
              <div style={{ width: 1, background: 'var(--border)' }} />
              <div style={{ textAlign: 'center', flex: 1 }}>
                <p style={{ fontSize: '1rem', fontWeight: 700 }}>
                  {trip.end_mileage.toLocaleString()}
                </p>
                <p className="text-muted" style={{ fontSize: '0.72rem' }}>км одометр</p>
              </div>
            </>
          )}
        </div>

        {trip.has_damage && (
          <div style={{ marginTop: 8, padding: '4px 8px', background: 'var(--danger-light)',
            borderRadius: 'var(--r-sm)', color: 'var(--danger)', fontSize: '0.78rem',
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="warning" size={14} />
            Зафиксированы повреждения
          </div>
        )}
      </div>
    </div>
  )
}

export default function TripsPage() {
  const [trips, setTrips]     = useState([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    api.get('/trips/my', { params: { per_page: 50 } })
      .then(r => {
        const items = r.data?.data?.items || []
        // Обогащаем данными о машине
        return Promise.all(
          items.map(t =>
            t.car_id
              ? api.get(`/cars/${t.car_id}`).then(r2 => ({ ...t, car: r2.data?.data })).catch(() => t)
              : Promise.resolve(t)
          )
        )
      })
      .then(setTrips)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const active    = trips.filter(t => t.status === 'active')
  const pending   = trips.filter(t => t.status === 'finished_by_user')
  const confirmed = trips.filter(t => t.status === 'confirmed_by_admin')

  return (
    <div>
      <PageHeader title="Мои поездки" />
      <div className="p-page flex-col" style={{ gap: 'var(--gap-sm)' }}>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="spinner" style={{ width: 40, height: 40 }} />
          </div>
        )}

        {!loading && trips.length === 0 && (
          <div style={{ textAlign: 'center', paddingTop: '3rem' }}>
            <Icon name="trips" size={32} />
            <p style={{ fontWeight: 600, marginTop: 12 }}>Поездок пока нет</p>
            <p className="text-muted" style={{ fontSize: '0.88rem', marginTop: 6 }}>
              Выберите машину на главной и начните первую поездку
            </p>
          </div>
        )}

        {active.length > 0 && (
          <>
            <p className="section-title" style={{ color: 'var(--accent)' }}>Активная поездка</p>
            {active.map(t => <TripCard key={t.id} trip={t} onClick={() => navigate('/')} />)}
          </>
        )}

        {pending.length > 0 && (
          <>
            <p className="section-title" style={{ color: 'var(--warning)' }}>Ожидают подтверждения</p>
            {pending.map(t => (
              <TripCard key={t.id} trip={t}
                onClick={() => navigate(`/trips/${t.id}`)} />
            ))}
          </>
        )}

        {confirmed.length > 0 && (
          <>
            <p className="section-title">История</p>
            {confirmed.map(t => (
              <TripCard key={t.id} trip={t}
                onClick={() => navigate(`/trips/${t.id}`)} />
            ))}
          </>
        )}
      </div>
    </div>
  )
}
